import { computed } from 'vue';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/vue-query';
import { DateTime } from 'luxon';
import { getCoreAxiosInstance } from '@utils/axios';
import { normalizeEventFromAPI } from '@utils/timezone';
import { googleCopyId } from '@utils/googleEvents';
import { queryClient as appClient } from './client';

const api = getCoreAxiosInstance();

// Every cached list of our events sits under calendarKeys.events(), so one
// invalidation refreshes the calendar's ranges and the home page's day alike.
export const calendarKeys = {
  events: () => ['calendar', 'events'],
  range: (from, to) => ['calendar', 'events', { from, to }],
  day: (date) => ['calendar', 'events', { date }],
  googleEvents: () => ['calendar', 'google'],
  google: (from, to) => ['calendar', 'google', { from, to }],
  connection: () => ['calendar', 'connection'],
  categories: () => ['calendar', 'categories'],
};

const toEvent = (res) => normalizeEventFromAPI(res.data);

export const eventsQuery = (params) => ({
  queryKey: params.date ? calendarKeys.day(params.date) : calendarKeys.range(params.from, params.to),
  queryFn: () => api.get('/calendar', { params }).then((res) => res.data.map(normalizeEventFromAPI)),
});

// Google's events in the range, and ours the API just corrected from Google,
// which go into every list. An API from before the sync answers with the bare list.
// Stale once the API would read Google's changes again (every 10s at most).
export const googleEventsQuery = ({ from, to }, client = appClient) => ({
  queryKey: calendarKeys.google(from, to),
  queryFn: async () => {
    const { data } = await api.get('/calendar/google', { params: { from, to } });
    if (Array.isArray(data)) return { items: data };
    const synced = data.synced.map(normalizeEventFromAPI);
    if (synced.length) showSavedEvents(synced, client);
    return { items: data.items };
  },
  staleTime: 10 * 1000,
});

export const googleConnectionQuery = () => ({
  queryKey: calendarKeys.connection(),
  queryFn: () => api.get('/calendar/google/status').then((res) => res.data || { pairing: false }),
});

// Lists holding a saved event show it at once; then every list refetches, which
// is also where a new one appears.
export const showSavedEvents = (events, client = appClient) => {
  const byId = new Map(events.map((event) => [event.id, event]));
  client.setQueriesData({ queryKey: calendarKeys.events() }, (list) =>
    list?.map((x) => byId.get(x.id) ?? x)
  );
  return client.invalidateQueries({ queryKey: calendarKeys.events() });
};

// Its Google copy goes too: the calendar hides it only while the event is
// loaded, so it would show as a Google event until refetched.
export const hideDeletedEvent = (event, client = appClient) => {
  const copyId = googleCopyId(event);
  client.setQueriesData({ queryKey: calendarKeys.events() }, (list) => list?.filter((x) => x.id !== event.id));
  client.setQueriesData({ queryKey: calendarKeys.googleEvents() }, (data) =>
    data && { ...data, items: data.items.filter((x) => x.id !== copyId) }
  );
  // Not awaited: the copy is already gone, and the panel shouldn't wait on Google.
  client.invalidateQueries({ queryKey: calendarKeys.googleEvents() });
  return client.invalidateQueries({ queryKey: calendarKeys.events() });
};

// The same for an event that lives only in Google, by Google's copy of it.
export const showMovedGoogleEvent = (item, client = appClient) => {
  client.setQueriesData({ queryKey: calendarKeys.googleEvents() }, (data) =>
    data && { ...data, items: data.items.map((x) => (x.id === item.id ? item : x)) }
  );
  return client.invalidateQueries({ queryKey: calendarKeys.googleEvents() });
};

// The days a FullCalendar view shows, the month grid's days from neighbouring
// months included. `to` is the day after the last; /calendar reads it as
// inclusive, so ours come with one extra day that FullCalendar doesn't draw.
export const viewRange = (view, zone) => ({
  from: DateTime.fromJSDate(view.activeStart, { zone }).toISODate(),
  to: DateTime.fromJSDate(view.activeEnd, { zone }).toISODate(),
});

// `range`: a ref of { from, to }, empty until the calendar reports one.
export const useCalendarEvents = (range) => {
  const hasRange = () => !!range.value.from && !!range.value.to;
  const { data, error } = useQuery(() => ({
    ...eventsQuery(range.value),
    enabled: hasRange(),
    placeholderData: keepPreviousData,
  }));
  return { events: computed(() => data.value ?? []), error };
};

export const useDayEvents = (date) => {
  const { data, isPending } = useQuery(eventsQuery({ date }));
  return { events: computed(() => data.value ?? []), isPending };
};

export const useGoogleConnection = () => {
  const { data, error } = useQuery(googleConnectionQuery());
  return { connection: data, error };
};

// Each load is also when the API applies what changed in Google, so an open
// calendar catches up within a minute.
const GOOGLE_POLL_EVERY = 60 * 1000;

// [] unless the connection is paired. Refetched every minute while the tab is
// visible; vue-query skips hidden tabs.
export const useGoogleEvents = (range, connection) => {
  const paired = () => !!connection.value?.pairing;
  const { data, error } = useQuery(() => ({
    ...googleEventsQuery(range.value),
    refetchInterval: GOOGLE_POLL_EVERY,
    enabled: !!range.value.from && !!range.value.to && paired(),
    placeholderData: keepPreviousData,
  }));
  return { items: computed(() => (paired() ? data.value?.items ?? [] : [])), error };
};

// { id, event }: a new event without an id. An update can send only what changed.
export const saveEvent = ({ id, event }) =>
  (id ? api.put(`/calendar/${id}`, event) : api.post('/calendar', event)).then(toEvent);

export const setEventCancelled = ({ id, isCancelled }) =>
  api.put(`/calendar/${id}/status`, { isCancelled }).then(toEvent);

// { id, start, end, notify }: `notify` has Google email the guests.
export const moveGoogleEvent = ({ id, ...body }) =>
  api.patch(`/calendar/google/events/${encodeURIComponent(id)}`, body).then((res) => res.data);

export const useSaveEvent = () =>
  useMutation({ mutationFn: saveEvent, onSuccess: (event) => showSavedEvents([event]) });

export const useSetEventCancelled = () =>
  useMutation({ mutationFn: setEventCancelled, onSuccess: (event) => showSavedEvents([event]) });

export const useMoveGoogleEvent = () =>
  useMutation({ mutationFn: moveGoogleEvent, onSuccess: (item) => showMovedGoogleEvent(item) });

export const useDeleteEvent = () =>
  useMutation({
    mutationFn: (event) => api.delete(`/calendar/${event.id}`),
    onSuccess: (_, event) => hideDeletedEvent(event),
  });

// The switch flips at once, and back if the API refuses.
export const useSetGoogleTwoWaySync = () =>
  useMutation({
    mutationFn: (twoWaySync) => api.patch('/calendar/google/status', { two_way_sync: twoWaySync }),
    onMutate: async (twoWaySync) => {
      await appClient.cancelQueries({ queryKey: calendarKeys.connection() });
      const previous = appClient.getQueryData(calendarKeys.connection());
      appClient.setQueryData(calendarKeys.connection(), previous && { ...previous, two_way_sync: twoWaySync });
      return previous;
    },
    onError: (_, __, previous) => appClient.setQueryData(calendarKeys.connection(), previous),
    onSettled: () => appClient.invalidateQueries({ queryKey: calendarKeys.connection() }),
  });

export const useDisconnectGoogle = () =>
  useMutation({
    mutationFn: () => api.delete('/calendar/google/authorize'),
    onSuccess: () => {
      appClient.setQueryData(calendarKeys.connection(), { pairing: false });
      return appClient.invalidateQueries({ queryKey: calendarKeys.connection() });
    },
  });

// The user's labels for appointments. Only the calendar settings change them, and they update this
// cache themselves; a change made on another device shows once the list is this old.
export const eventCategoriesQuery = () => ({
  queryKey: calendarKeys.categories(),
  queryFn: () => api.get('/calendar/categories').then((res) => res.data),
  staleTime: 10 * 60 * 1000,
});

// [] until they load; `limit` is how many the user may have. A panel showing them calls
// `retryCategoriesIfFailed` when it opens; it rejects if the list still doesn't load.
export const useEventCategories = () => {
  const { data, isError, refetch } = useQuery(eventCategoriesQuery());
  return {
    categories: computed(() => data.value?.categories ?? []),
    limit: computed(() => data.value?.limit ?? 10),
    categoriesFailed: isError,
    retryCategoriesIfFailed: () => (isError.value ? refetch({ throwOnError: true }) : Promise.resolve()),
  };
};

export const saveEventCategory = ({ id, name, color }) =>
  (id ? api.put(`/calendar/categories/${id}`, { name, color }) : api.post('/calendar/categories', { name, color }))
    .then((res) => res.data);

// A save while the list hasn't loaded fetches it, now with the saved category.
const showSavedCategory = (category, client = appClient) => {
  if (!client.getQueryData(calendarKeys.categories())) return client.invalidateQueries({ queryKey: calendarKeys.categories() });
  client.setQueryData(calendarKeys.categories(), (data) => ({
    ...data,
    categories: data.categories.some((c) => c.id === category.id)
      ? data.categories.map((c) => (c.id === category.id ? category : c))
      : [...data.categories, category],
  }));
};

export const useSaveEventCategory = () =>
  useMutation({ mutationFn: saveEventCategory, onSuccess: (category) => showSavedCategory(category) });
